import { useState, useEffect } from 'react'
import { apiClient } from '@/lib/api'
import { cognitoAuth } from '@/lib/cognito'

function getSubdomainTenant(): string | null {
  const hostname = window.location.hostname
  
  // Skip admin and api subdomains (including dev/test environments)
  if (hostname === 'admin.queuemein.com' || hostname === 'api.queuemein.com' || hostname === 'queuemein.com' ||
      hostname === 'admin.dev.queuemein.com' || hostname === 'admin.test.queuemein.com' ||
      hostname === 'api.dev.queuemein.com' || hostname === 'api.test.queuemein.com') {
    return null
  }
  
  // Extract subdomain
  if (hostname.endsWith('.queuemein.com')) {
    let subdomain = hostname.replace('.queuemein.com', '')
    
    // Handle dev/test environments - extract tenant from test1.dev format
    if (subdomain.endsWith('.dev') || subdomain.endsWith('.test')) {
      subdomain = subdomain.split('.')[0]
    }
    
    // Validate subdomain format
    if (/^[a-zA-Z0-9_-]+$/.test(subdomain)) {
      return subdomain
    }
  }
  
  return null
}

export function useAuth() {
  const [user, setUser] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [impersonationData, setImpersonationData] = useState<any>(null)
  const [subdomainTenant, setSubdomainTenant] = useState<string | null>(null)

  useEffect(() => {
    // Detect subdomain tenant
    const detectedSubdomain = getSubdomainTenant()
    setSubdomainTenant(detectedSubdomain)
    
    const fetchUser = async () => {
      try {
        if (cognitoAuth.isAuthenticated()) {
          // Check for impersonation first
          const storedImpersonation = localStorage.getItem('impersonation')
          if (storedImpersonation) {
            try {
              const impersonation = JSON.parse(storedImpersonation)
              setImpersonationData(impersonation)
              
              // For impersonation, we'll use the impersonated user's data
              // but keep the original user's permissions and token
              const userInfo = cognitoAuth.getUserInfo()
              const token = cognitoAuth.getIdToken()
              
              if (userInfo && token) {
                // CRITICAL: Set the original user's token for API authentication
                apiClient.setToken(token)
                
                // Merge original user permissions with impersonated user info
                const impersonatedUser = {
                  ...userInfo, // Keep original permissions and token
                  email: impersonation.impersonatedUser.email,
                  first_name: impersonation.impersonatedUser.name.split(' ')[0],
                  last_name: impersonation.impersonatedUser.name.split(' ').slice(1).join(' '),
                  tenant_from_group: impersonation.impersonatedUser.tenant_id, // Use impersonated user's tenant
                  role: impersonation.impersonatedUser.role, // Use impersonated user's role
                  _isImpersonating: true,
                  _originalUser: impersonation.originalUser,
                  _impersonatedUser: impersonation.impersonatedUser
                }
                setUser(impersonatedUser)
              }
            } catch (impErr) {
              console.error('Failed to parse impersonation data:', impErr)
              localStorage.removeItem('impersonation')
            }
          } else {
            // Normal authentication flow
            const userInfo = cognitoAuth.getUserInfo()
            if (userInfo) {
              // Validate subdomain access before setting user
              if (detectedSubdomain) {
                const userTenant = userInfo.tenant_from_group
                const isSuperAdmin = userInfo.groups?.includes('SUPER_ADMIN')
                
                // If not super admin and tenant doesn't match subdomain, logout
                if (!isSuperAdmin && userTenant?.toUpperCase() !== detectedSubdomain.toUpperCase()) {
                  console.error('Tenant mismatch: user belongs to', userTenant, 'but accessing', detectedSubdomain)
                  
                  // Show user-friendly message
                  const userDomain = userTenant?.toLowerCase()
                  const env = window.location.hostname.includes('.dev.') ? '.dev' : 
                             window.location.hostname.includes('.test.') ? '.test' : ''
                  const correctUrl = `https://${userDomain}${env}.queuemein.com`
                  
                  alert(
                    `Access Denied\n\n` +
                    `You are logged in as a user for tenant "${userTenant}", but you are trying to access tenant "${detectedSubdomain}".\n\n` +
                    `Please log in at your correct tenant domain:\n${correctUrl}`
                  )
                  
                  setError('You do not have access to this tenant')
                  cognitoAuth.logout()
                  return
                }
              }
              
              setUser(userInfo)
              
              // Try to enhance with API data, but don't fail if API is unavailable
              try {
                const token = cognitoAuth.getIdToken()
                if (token) {
                  apiClient.setToken(token)
                  const userData = await apiClient.getMe()
                  setUser(userData)
                }
              } catch (apiErr: any) {
                // If API returns 403, user doesn't have access to this subdomain
                if (apiErr?.response?.status === 403) {
                  console.error('Access denied to subdomain')
                  setError('You do not have access to this tenant')
                  cognitoAuth.logout()
                  return
                }
                console.warn('API call failed, using token data:', apiErr)
                // Keep the user info from token
              }
            }
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Auth failed')
        console.error('Auth error:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchUser()
    
    // Listen for impersonation events
    const handleImpersonationStart = () => {
      fetchUser()
    }
    
    const handleImpersonationEnd = () => {
      setImpersonationData(null)
      fetchUser()
    }
    
    window.addEventListener('impersonation-started', handleImpersonationStart)
    window.addEventListener('impersonation-ended', handleImpersonationEnd)
    
    return () => {
      window.removeEventListener('impersonation-started', handleImpersonationStart)
      window.removeEventListener('impersonation-ended', handleImpersonationEnd)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const login = () => cognitoAuth.login()
  const logout = () => {
    setUser(null)
    cognitoAuth.logout()
  }
  const isAuthenticated = cognitoAuth.isAuthenticated()
  const isSuperAdmin = user?.groups?.includes('SUPER_ADMIN') && !subdomainTenant // Super admin only on admin.queuemein.com
  const tenantId = subdomainTenant || user?.tenant_from_group // Use subdomain tenant if available
  
  // Role checking helpers
  const userRole = user?.role?.toLowerCase() || ''
  const isTenantAdmin = userRole === 'tenant_admin' || userRole === 'admin'
  const isTenantManager = userRole === 'tenant_manager' || userRole === 'manager'
  const isViewer = userRole === 'viewer'
  

  


  const refreshUser = async () => {
    try {
      if (cognitoAuth.isAuthenticated()) {
        const token = cognitoAuth.getIdToken()
        if (token) {
          apiClient.setToken(token)
          const userData = await apiClient.getMe()
          setUser((prevUser: any) => ({ ...prevUser, ...userData }))
        }
      }
    } catch (err) {
      console.warn('Failed to refresh user data:', err)
    }
  }

  return {
    user,
    loading,
    error,
    isAuthenticated,
    isSuperAdmin,
    isTenantAdmin,
    isTenantManager,
    isViewer,
    tenantId,
    impersonationData,
    isImpersonating: !!impersonationData,
    login,
    logout,
    refreshUser,
  }
}

export function useTenants() {
  const [tenants, setTenants] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchTenants = async () => {
    try {
      setLoading(true)
      
      // Check if we're on a tenant subdomain
      const subdomainTenant = getSubdomainTenant()
      
      if (subdomainTenant) {
        // For tenant subdomains, only return that specific tenant
        // Don't call any API - just return the subdomain tenant
        const tenantData = {
          tenant_id: subdomainTenant,
          display_name: subdomainTenant.charAt(0).toUpperCase() + subdomainTenant.slice(1),
          status: 'active',
          pk: `t_${subdomainTenant}#tenant`,
          sk: 'profile'
        }
        setTenants([tenantData])
      } else {
        // For admin.queuemein.com, call the global tenants endpoint (SUPER_ADMIN only)
        const response = await apiClient.getTenants()
        // Filter to only show tenant profile records, not queue records
        const filteredTenants = (response.tenants || [])
          .filter(t => t && (t.sk === 'profile' || t.tenant_id))
          .filter(t => t.tenant_id || (t.pk && t.sk === 'profile'))
        setTenants(filteredTenants)
      }
    } catch (err) {
      // For tenant subdomains, if there's an error, still provide the tenant data
      const subdomainTenant = getSubdomainTenant()
      if (subdomainTenant) {
        const tenantData = {
          tenant_id: subdomainTenant,
          display_name: subdomainTenant.charAt(0).toUpperCase() + subdomainTenant.slice(1),
          status: 'active',
          pk: `t_${subdomainTenant}#tenant`,
          sk: 'profile'
        }
        setTenants([tenantData])
      } else {
        setError(err instanceof Error ? err.message : 'Failed to fetch tenants')
        console.error('Failed to fetch tenants:', err)
        setTenants([])
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTenants()
    
    // Listen for tenant updates from other components
    const handleTenantsUpdated = () => {
      fetchTenants()
    }
    
    window.addEventListener('tenants-updated', handleTenantsUpdated)
    
    return () => {
      window.removeEventListener('tenants-updated', handleTenantsUpdated)
    }
  }, [])

  return {
    tenants,
    loading,
    error,
    refetch: fetchTenants,
  }
}

export function useQueues(tenantId: string | null) {
  const [queues, setQueues] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchQueues = async () => {
    if (!tenantId) return

    try {
      setLoading(true)
      const response = await apiClient.getQueuesSummary(tenantId)
      setQueues(response.queues || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch queues')
      // Mock data for development
      setQueues([
        { location_id: 'main', service_id: 'support', waiting: 5, called: 1, being_serviced: 2, no_show: 0, archived: false },
        { location_id: 'main', service_id: 'premium', waiting: 2, called: 0, being_serviced: 1, no_show: 1, archived: false },
      ])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchQueues()
  }, [tenantId])
  
  // Listen for tenant changes and refresh data
  useEffect(() => {
    const handleTenantChange = () => {
      fetchQueues()
    }
    
    window.addEventListener('tenant-changed', handleTenantChange)
    
    return () => {
      window.removeEventListener('tenant-changed', handleTenantChange)
    }
  }, [])

  return {
    queues,
    loading,
    error,
    refetch: fetchQueues,
  }
}